import isNumber from 'lodash/isNumber';
import uniqBy from 'lodash/uniqBy';

import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
} from '@revelio/core/composition-buckets';
import {
  type CompositionV3BreakdownOptions,
  type CompositionV3DataQuery,
  type CompositionV3Ent,
  EntKind,
  type GetPayV3Query,
  type PayV3Req,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3Filters } from '../../filters/utils/composition-v3/getCompositionV3Filters';
import {
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../filters/utils/composition-v3/getCompositionV3Variables';
import { resolveV3EntForSelection } from '../../filters/utils/get-v3-entities';
import { getPayV3Variables } from '../../filters/utils/pay-v3/get-pay-v3-variables';
import { useChart } from '../Chart/ChartProvider';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import {
  type PlotFilter,
  PlotFilterType,
} from '../PageConfigProvider/types/item';
import { usePageState } from '../PageState/PageStateProvider';
import { usePayData } from './compensation/use-pay-data';
import { CompositionRawDataProvider } from './composition/CompositionRawDataProvider';
import { TOP_COMPANIES_MAX, TOP_N_LIMIT } from './constants';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';

type TopNDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  children: DataProviderChildren<TopEntity[] | undefined>;
};

export type SupportedSecondaryDimensions = Extract<
  SecondaryDimensionValues,
  'company' | 'geography' | 'role'
>;

export type TopEntity = {
  EntID: number;
  EntType: EntKind;
};

export const TopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  children,
}: TopNDataProviderProps) => {
  const { item } = useChart();
  const plotFilters = item?.plot?.plotFilters;
  const entityLimit = getTopNEntityLimit(plotFilters);

  if (secondaryDimension === 'company') {
    return (
      <PayTopCompaniesProvider
        primaryDimension={primaryDimension}
        limit={getTopNEntityLimit(plotFilters, TOP_COMPANIES_MAX)}
      >
        {children}
      </PayTopCompaniesProvider>
    );
  }

  return (
    <CompositionTopNProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
      limit={entityLimit}
    >
      {children}
    </CompositionTopNProvider>
  );
};

export const getTopNFilterValue = (
  plotFilters?: PlotFilter[]
): number | undefined => {
  const topNFilterValue = plotFilters?.find(
    (filter) => filter.type === PlotFilterType.TOP_N
  )?.value;
  return isNumber(topNFilterValue) &&
    Number.isFinite(topNFilterValue) &&
    topNFilterValue > 0
    ? topNFilterValue
    : undefined;
};

export const getTopNEntityLimit = (
  plotFilters?: PlotFilter[],
  maximum = Number.POSITIVE_INFINITY
): number => Math.min(getTopNFilterValue(plotFilters) ?? TOP_N_LIMIT, maximum);

const PayTopCompaniesProvider = ({
  primaryDimension,
  limit,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  limit: number;
  children: DataProviderChildren<TopEntity[] | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const { usesStateBreakdownOverMsa, skillTaxonomyId, skillTaxonomyVersion } =
    useFilterContext();
  const { item } = useChart();
  const { pageState } = usePageState();

  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const selections = primaryFilter?.selections ?? [];

  // Narrow the selections to the multi-primary entity when one is chosen.
  const narrowedSelections = selections.filter((selection) =>
    selectedEntity
      ? selection.levelId === selectedEntity.levelId &&
        selection.id.split('_').slice(-1)[0] === String(selectedEntity.id)
      : true
  );

  const entities = narrowedSelections
    .map((selection) => resolveV3EntForSelection(selection))
    .filter((ent): ent is CompositionV3Ent => ent !== null);

  const { customSkillTree } = useCustomSkillTaxonomyTree(
    skillTaxonomyId,
    skillTaxonomyVersion
  );
  const hasCustomSkillSelections = effectiveFilters.some(
    (f) =>
      f.id === FilterId.CUSTOM_SKILL &&
      f.type === FilterType.TREE &&
      f.selections.length > 0
  );
  const isWaitingForSkillTaxonomy =
    hasCustomSkillSelections && customSkillTree === null;

  const req: PayV3Req = {
    ...getPayV3Variables({
      primaryDimension,
      effectiveFilters,
      entitiesOverride: entities,
    }),
    options: {
      required_top_companies_length: TOP_COMPANIES_MAX,
      ...(usesStateBreakdownOverMsa && {
        geo_breakdown_state_over_msa: true,
      }),
    },
  };

  const { data, status } = usePayData({
    variant: 'full',
    req,
    pause: isWaitingForSkillTaxonomy || entities.length === 0,
  });

  const topEntities = data?.pay_v3
    ? selectTopCompanyPool(data.pay_v3, limit)
    : undefined;

  return children({ data: topEntities, status });
};

/**
 * Builds the company entity list the Top Companies chart sends to
 * `composition_v3`. pay_v3 returns up to `TOP_COMPANIES_MAX` companies for
 * EACH primary selection, so the union has to be bounded before it becomes
 * `entities` — go-api rejects an oversized list outright.
 *
 * The per-selection lists are interleaved by rank rather than concatenated:
 * capping a flat concatenation would fill the pool from the first selection
 * alone and hide the other selections' companies entirely, which is the
 * failure the per-selection fetch was introduced to solve.
 */
export const selectTopCompanyPool = (
  pay: GetPayV3Query['pay_v3'] | undefined,
  limit: number = TOP_COMPANIES_MAX
): TopEntity[] => {
  if (!pay) return [];
  const perSelection = pay.entities.map(
    (entity) => entity.metrics.top_companies ?? []
  );
  const deepest = perSelection.reduce(
    (max, companies) => Math.max(max, companies.length),
    0
  );

  const byRank: TopEntity[] = [];
  for (let rank = 0; rank < deepest; rank++) {
    for (const companies of perSelection) {
      const company = companies[rank];
      if (company) {
        byRank.push({ EntID: company.id, EntType: EntKind.Company });
      }
    }
  }

  return uniqBy(byRank, 'EntID').slice(0, limit);
};

export const getTopCompaniesFromPay = (
  pay: GetPayV3Query['pay_v3'] | undefined
): TopEntity[] | undefined => {
  if (!pay) return undefined;
  const topCompanies = pay.entities.flatMap(
    (entity) => entity.metrics.top_companies ?? []
  );
  return uniqBy(
    topCompanies.map(
      (company): TopEntity => ({
        EntID: company.id,
        EntType: EntKind.Company,
      })
    ),
    'EntID'
  );
};

const CompositionTopNProvider = ({
  primaryDimension,
  secondaryDimension,
  limit,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: Exclude<SupportedSecondaryDimensions, 'company'>;
  limit: number;
  children: DataProviderChildren<TopEntity[] | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { geographySelectionListIds, skillTaxonomyId, skillTaxonomyVersion } =
    useFilterContext();
  const { customSkillTree } = useCustomSkillTaxonomyTree(
    skillTaxonomyId,
    skillTaxonomyVersion
  );

  const normalizedEffectiveFilters =
    normalizeCompositionEffectiveFilters(effectiveFilters);
  const customSkillDeepestLevel = getCustomSkillDeepestLevelFromFilters(
    normalizedEffectiveFilters
  );
  const baseFilters = getCompositionV3Filters({
    primaryDimension,
    isSnapshot: true,
    effectiveFilters: normalizedEffectiveFilters,
  });

  const { item } = useChart();
  const plotFilters = item?.plot?.plotFilters;
  const breakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: baseFilters,
    customSkillTree,
    customSkillDeepestLevel,
    geographySelectionListIds,
    includedBreakdownDimensions: [
      SECONDARY_DIMENSION_TO_BREAKDOWN[secondaryDimension],
    ],
    plotFilters,
    secondaryDimension,
  });
  const entType = getSegmentBreakdownEntKind(
    secondaryDimension,
    breakdownOptions
  );

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      getSelectionBreakdowns
    >
      {({ data, status }) => {
        const composition = data?.composition_v3 as
          | NonNullable<CompositionV3DataQuery['composition_v3']>
          | null
          | undefined;
        const topEntities =
          composition && entType
            ? getTopEntitiesFromComposition({
                composition,
                segment: secondaryDimension,
                entType,
              })?.slice(0, limit)
            : undefined;
        return children({
          data: topEntities,
          status: entType === null ? DataProviderQueryStatus.Error : status,
        });
      }}
    </CompositionRawDataProvider>
  );
};

const SECONDARY_DIMENSION_TO_BREAKDOWN: Record<
  Exclude<SupportedSecondaryDimensions, 'company'>,
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
};

const getSegmentBreakdownEntKind = (
  secondaryDimension: Exclude<SupportedSecondaryDimensions, 'company'>,
  breakdownOptions: CompositionV3BreakdownOptions
): EntKind | null => {
  switch (secondaryDimension) {
    case 'geography':
      return breakdownOptions.geo_breakdown_level ?? null;
    case 'role':
      return breakdownOptions.role_breakdown_level ?? null;
  }
};

export const getTopEntitiesFromComposition = ({
  composition,
  segment,
  entType,
}: {
  composition: NonNullable<CompositionV3DataQuery['composition_v3']>;
  segment: Exclude<SupportedSecondaryDimensions, 'company'>;
  entType: EntKind;
}): TopEntity[] => {
  const breakdownRows = composition.entities.flatMap(
    (entity) => entity.metrics[segment] ?? []
  );

  const ranked = breakdownRows
    .filter(
      (row) =>
        !isEmptyCompositionBucket(row.id) && !isOtherCompositionBucket(row.id)
    )
    .sort((a, b) => a.rank - b.rank);

  return uniqBy(
    ranked.map((row): TopEntity => ({ EntID: row.id, EntType: entType })),
    'EntID'
  );
};
